import React from 'react';
import { useEditorStore, ErrorCode } from '../../stores/editorStore';

const ErrorTooltip: React.FC = () => {
  const {
    edits,
    activeEditIndex,
    hoveredEditIndex,
    tooltipPosition,
    applyEdit,
    applyGroupEdits,
    setActiveEdit,
  } = useEditorStore();

  // Show tooltip for hovered or active edit
  // Prioritize hover - when user hovers over a different edit, show that one
  const editIndex = hoveredEditIndex ?? activeEditIndex;
  if (editIndex === null || !tooltipPosition) return null;

  const edit = edits[editIndex];
  if (!edit) return null;

  // Get error codes array (support both new array and legacy single code)
  const errorCodes: ErrorCode[] = edit.error_codes || (edit.error_code
    ? [{ code: edit.error_code, label: edit.label || 'Correction', explanation: edit.explanation }]
    : [{ code: 'UNK', label: edit.label || 'Correction', explanation: edit.explanation }]
  );

  // Get primary label for header (first error code)
  const primaryError = errorCodes[0];

  const handleApply = () => {
    // Check for both null and undefined (API returns null for no group)
    if (edit.group_id != null) {
      applyGroupEdits(edit.group_id);
    } else {
      applyEdit(editIndex);
    }
    setActiveEdit(null);
  };

  // Determine if this is a detailed view (clicked) or preview (hover)
  // Only show detailed view if the current tooltip is for the clicked edit
  const isDetailed = activeEditIndex !== null && activeEditIndex === editIndex;

  return (
    <div
      className="fixed z-50 animate-fade-in"
      style={{
        left: Math.min(tooltipPosition.x, window.innerWidth - 320),
        top: tooltipPosition.y,
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 max-w-sm">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="px-2 py-0.5 text-xs font-medium rounded bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-400">
                {edit.type}
              </span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {primaryError.label}
              </span>
            </div>
            {errorCodes.length > 1 && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                +{errorCodes.length - 1} more
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="px-4 py-3 space-y-3">
          {/* Original vs Correction */}
          <div className="space-y-2">
            {edit.original && (
              <div className="flex items-start space-x-2">
                <span className="text-red-500 text-sm">−</span>
                <span className="text-sm text-gray-600 dark:text-gray-300 line-through">
                  {edit.original}
                </span>
              </div>
            )}
            {edit.correction && (
              <div className="flex items-start space-x-2">
                <span className="text-green-500 text-sm">+</span>
                <span className="text-sm text-gray-900 dark:text-white font-medium">
                  {edit.correction}
                </span>
              </div>
            )}
          </div>

          {/* Stacked error codes list */}
          <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
            <ul className="space-y-1.5">
              {errorCodes.map((errCode, idx) => (
                <li key={idx} className="flex items-start space-x-2">
                  <span className="text-primary-500 text-sm mt-0.5">•</span>
                  <div className="flex-1">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {errCode.label}
                    </span>
                    {isDetailed && errCode.explanation && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {errCode.explanation}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Example (only in detailed view) */}
          {isDetailed && edit.example && (
            <div className="p-2 bg-gray-50 dark:bg-gray-900/50 rounded text-sm text-gray-600 dark:text-gray-400 italic">
              Example: {edit.example}
            </div>
          )}
        </div>

        {/* Actions (only in detailed view) */}
        {isDetailed && (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-2">
            <button
              onClick={() => setActiveEdit(null)}
              className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              Dismiss
            </button>
            <button
              onClick={handleApply}
              className="px-3 py-1.5 text-sm bg-primary-600 text-white hover:bg-primary-700 rounded"
            >
              Apply
            </button>
          </div>
        )}

        {/* Hint for hover state */}
        {!isDetailed && (
          <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Click for details, right-click to apply
            </p>
          </div>
        )}
      </div>

      {/* Arrow */}
      <div
        className="absolute w-3 h-3 bg-white dark:bg-gray-800 border-l border-t border-gray-200 dark:border-gray-700 transform -rotate-45"
        style={{ top: -6, left: 20 }}
      />
    </div>
  );
};

export default ErrorTooltip;
